/**
 * SEO 結構化數據生成工具 v5.0
 * 支援 Google Search、ChatGPT、Perplexity、Gemini、Claude 等 AI 搜尋引擎
 * 2026-03-07 最終強化版
 *
 * v5.0 新增：
 * ✅ generateHomePageSchema：加入 WebPage Schema 節點，讓首頁有完整的 @id 和 breadcrumb 起點
 * ✅ generateArticlePageSchema：Article @type 改為 ['Article','BlogPosting']（雙重類型，提升 AI 引用率）
 * ✅ 所有 Schema：加入 `potentialAction` ReadAction，明確告知 AI 此頁面可被閱讀
 */

const SITE_URL   = 'https://omakase.tw';
const SITE_NAME  = 'Omakase TW';
const SITE_ALT   = 'お任せ Omakase';
const LOGO_URL   = `${SITE_URL}/omakase-favicon.svg`;
const ORG_ID     = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;

// ── 內部工具：移除 @context，供 @graph 使用 ──
const strip = (s) => {
  const { '@context': _, ...rest } = s;
  return rest;
};

// ─────────────────────────────────────────
// Organization Schema
// ─────────────────────────────────────────
export function generateOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': ORG_ID,
    name: SITE_NAME,
    alternateName: SITE_ALT,
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      '@id': `${SITE_URL}/#logo`,
      url: LOGO_URL,
      width: 60,
      height: 60,
      caption: SITE_NAME,
    },
    description: '台灣最完整的 Omakase 日本料理文化指南，涵蓋入門知識、用餐禮儀、季節食材與深度文化解析。',
    foundingDate: '2025',
    inLanguage: 'zh-TW',
    knowsAbout: ['Omakase', '日本料理', '壽司文化', '懷石料理', '台灣美食文化', '季節食材'],
    sameAs: [
      'https://www.instagram.com/omakase.tw',
      'https://www.facebook.com/omakasetw',
    ],
  };
}

// ─────────────────────────────────────────
// WebSite Schema（含 SiteLinksSearchBox）
// ─────────────────────────────────────────
export function generateWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name: SITE_NAME,
    alternateName: SITE_ALT,
    url: SITE_URL,
    description: '台灣最完整的 Omakase 日本料理文化指南，涵蓋入門知識、用餐禮儀、季節食材與深度文化解析。',
    inLanguage: 'zh-TW',
    publisher: { '@id': ORG_ID },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/articles?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

// ─────────────────────────────────────────
// Breadcrumb Schema（@id 動態，避免多頁衝突）
// ─────────────────────────────────────────
export function generateBreadcrumbSchema(items, pageUrl) {
  const breadcrumbId = pageUrl
    ? `${SITE_URL}${pageUrl}#breadcrumb`
    : `${SITE_URL}/#breadcrumb`;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    '@id': breadcrumbId,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

// ─────────────────────────────────────────
// FAQ Schema
// ─────────────────────────────────────────
export function generateFAQSchema(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

// ─────────────────────────────────────────
// Article Schema（AI 搜尋強化版）
// ✅ v5.0：加入 potentialAction ReadAction，提升 AI 引用率
// ─────────────────────────────────────────
export function generateArticleSchema(article) {
  const fullUrl = `${SITE_URL}${article.url || ''}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${fullUrl}#article`,
    headline: article.title,
    description: article.description || article.excerpt || '',
    image: {
      '@type': 'ImageObject',
      '@id': `${fullUrl}#primaryimage`,
      url: article.image,
      width: 1200,
      height: 630,
      caption: article.title,
    },
    datePublished: article.datePublished || article.publishDate || '',
    dateModified:  article.dateModified  || article.modifiedDate || article.datePublished || '',
    author: {
      '@type': 'Person',
      '@id': `${SITE_URL}/#author-${encodeURIComponent(article.author || 'omakase')}`,
      name: article.author || SITE_NAME,
      url: SITE_URL,
      jobTitle: '日本料理文化研究者',
      worksFor: { '@id': ORG_ID },
    },
    publisher: { '@id': ORG_ID },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': fullUrl,
    },
    isPartOf: { '@id': WEBSITE_ID },
    // ✅ Speakable：讓 Google Assistant / AI 語音摘要優先讀取這些區塊
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['h1', 'h2', '.article-summary', 'article p:first-of-type'],
    },
    // ✅ ReadAction：明確告知 AI 此頁面可被閱讀引用
    potentialAction: {
      '@type': 'ReadAction',
      target: [fullUrl],
    },
    inLanguage: 'zh-TW',
    isAccessibleForFree: true,
    keywords: article.keywords || article.metaKeywords || '',
    about: {
      '@type': 'Thing',
      name: 'Omakase',
      description: '日本料理中由廚師主導菜單的高級用餐體驗',
      sameAs: 'https://en.wikipedia.org/wiki/Omakase',
    },
    mentions: [
      { '@type': 'Thing', name: '日本料理', sameAs: 'https://zh.wikipedia.org/wiki/日本料理' },
      { '@type': 'Place', name: '台灣',     sameAs: 'https://zh.wikipedia.org/wiki/台灣' },
    ],
    articleSection: article.section || article.category || 'Omakase 文化',
    copyrightYear: new Date(article.datePublished || article.publishDate || Date.now()).getFullYear(),
    copyrightHolder: { '@id': ORG_ID },
  };
}

// ─────────────────────────────────────────
// 首頁 @graph Schema
// ✅ v5.0：新增 WebPage 節點，讓首頁有完整 @id 和 primaryImageOfPage
// ─────────────────────────────────────────
export function generateHomePageSchema(faqData) {
  const orgSchema  = generateOrganizationSchema();
  const webSchema  = generateWebSiteSchema();
  const faqSchema  = generateFAQSchema(faqData);

  // ✅ 新增：首頁 WebPage Schema
  const webPageSchema = {
    '@type': 'WebPage',
    '@id': `${SITE_URL}/#webpage`,
    url: `${SITE_URL}/`,
    name: 'Omakase 完全指南 - 從入門到精通的日式料理體驗',
    description: '探索 Omakase 的精髓！從基礎知識到進階技巧，了解日本料理文化、用餐禮儀、季節食材與頂級食材的秘密。',
    inLanguage: 'zh-TW',
    isPartOf: { '@id': WEBSITE_ID },
    about: { '@id': ORG_ID },
    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: 'https://images.unsplash.com/photo-1579952363873-27d3bfad9c0d?w=1200&h=630&fit=crop',
      width: 1200,
      height: 630,
    },
    // ✅ ReadAction：AI 搜尋引擎知道此頁可被引用
    potentialAction: {
      '@type': 'ReadAction',
      target: [`${SITE_URL}/`],
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      '@id': `${SITE_URL}/#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首頁', item: `${SITE_URL}/` },
      ],
    },
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [
      strip(orgSchema),
      strip(webSchema),
      webPageSchema,
      strip(faqSchema),
    ],
  };
}

// ─────────────────────────────────────────
// 文章頁 @graph Schema
// ─────────────────────────────────────────
export function generateArticlePageSchema(article, breadcrumbItems) {
  const articleSchema    = generateArticleSchema(article);
  const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbItems, `/${article.slug || ''}`);
  const orgSchema        = generateOrganizationSchema();
  const webSchema        = generateWebSiteSchema();

  return {
    '@context': 'https://schema.org',
    '@graph': [
      strip(orgSchema),
      strip(webSchema),
      strip(breadcrumbSchema),
      strip(articleSchema),
    ],
  };
}

// ─────────────────────────────────────────
// 文章列表頁 @graph Schema
// ─────────────────────────────────────────
export function generateArticleListPageSchema(articles) {
  const orgSchema  = generateOrganizationSchema();
  const webSchema  = generateWebSiteSchema();
  const breadcrumb = generateBreadcrumbSchema(
    [
      { name: '首頁',     url: `${SITE_URL}/` },
      { name: '深度文章', url: `${SITE_URL}/articles` },
    ],
    '/articles'
  );

  const collectionSchema = {
    '@type': 'CollectionPage',
    '@id': `${SITE_URL}/articles#webpage`,
    name: 'Omakase 深度文章',
    description: '探索 Omakase 日本料理文化的深度文章，涵蓋入門指南、季節食材、禮儀文化等主題。',
    url: `${SITE_URL}/articles`,
    inLanguage: 'zh-TW',
    isAccessibleForFree: true,
    isPartOf: { '@id': WEBSITE_ID },
    breadcrumb: { '@id': `${SITE_URL}/articles#breadcrumb` },
    potentialAction: {
      '@type': 'ReadAction',
      target: [`${SITE_URL}/articles`],
    },
    about: {
      '@type': 'Thing',
      name: 'Omakase',
      sameAs: 'https://en.wikipedia.org/wiki/Omakase',
    },
    hasPart: articles.map(article => ({
      '@type': 'Article',
      '@id': `${SITE_URL}/${article.slug}#article`,
      headline: article.title,
      description: article.excerpt,
      url: `${SITE_URL}/${article.slug}`,
      datePublished: article.publishDate,
      dateModified:  article.modifiedDate || article.publishDate,
      author: { '@type': 'Person', name: article.author },
      image: { '@type': 'ImageObject', url: article.image },
    })),
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [
      strip(orgSchema),
      strip(webSchema),
      strip(breadcrumb),
      collectionSchema,
    ],
  };
}

// ── 舊版兼容 ──
export function generateArticleListSchema(articles) {
  return generateArticleListPageSchema(articles);
}

// ─────────────────────────────────────────
// HowTo Schema
// ─────────────────────────────────────────
export function generateHowToSchema({ name, description, steps, totalTime }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    description,
    totalTime,
    inLanguage: 'zh-TW',
    isAccessibleForFree: true,
    about: {
      '@type': 'Thing',
      name: 'Omakase',
      sameAs: 'https://en.wikipedia.org/wiki/Omakase',
    },
    step: steps.map((step, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: step.name,
      text: step.text,
    })),
  };
}